// Reloads the page whenever its analytics consent doesn't match what the server rendered it with.
//
// Engage permissions are decided server-side per request: ModulePermissions.AnalyticsIsAllowed
// reads "analytics:yes" from the CookieYes consent cookie, and gates both the server-side
// pageview tracking and umbracoEngage.analytics.js (Layout.cshtml, script[data-engage-analytics]).
// Reloading makes the page match the consent (as Engage's docs require, with location.reload(),
// which keeps the referrer and query string): accepting gets this visit tracked, revoking drops
// the already-loaded analytics script so it stops reporting. The reloaded page is measured as a
// new navigation in field data, with the banner gone, so it costs nothing in Core Web Vitals.
//
// Checked on start too, not only on cookieyes_consent_update:
// - CookieYes writes its cookie SameSite=Strict, so a visitor who consented earlier and arrives
//   through a link on another site (a search result, an email) sends no consent with that first
//   request - the server renders without analytics although the cookie says yes. The reload is
//   started by this page, so the cookie goes along.
// - A visitor can accept in the banner before this module runs (CookieYes loads async in <head>).
//
// Never more than one reload in a row for the same page: if the server still disagrees after it
// (a browser that doesn't send the cookie, a cookie domain not matching the host), an
// unconditional reload would never settle - the page would reload forever. The guard is cleared
// as soon as page and cookie agree, so a later consent change on that page reloads again.
// CookieYes also fires cookieyes_consent_update without a visitor action (e.g. on load); with
// nothing changed that's a no-op.
//
// Without a CookieYes site id (local development), cookie-consent-debug.ts stands in for it and
// fires the same event.

import { ENGAGE_SCRIPT_SELECTOR } from "../analytics/engage-events";
import { readConsentCookie } from "./cookie-consent-cookie";

const RELOADED_KEY = "cookie-consent-reloaded";

function analyticsAllowed(): boolean {
    return readConsentCookie().get("analytics") === "yes";
}

function reloadedFor(): string | null {
    try {
        return sessionStorage.getItem(RELOADED_KEY);
    } catch {
        return null;
    }
}

function setReloadedFor(href: string | null): void {
    try {
        if (href === null) {
            sessionStorage.removeItem(RELOADED_KEY);
        } else {
            sessionStorage.setItem(RELOADED_KEY, href);
        }
    } catch {
        // No storage (private mode, blocked): the guard is skipped, which only matters if the
        // server keeps disagreeing.
    }
}

export function initCookieConsent(): void {
    const renderedWithAnalytics = document.querySelector(ENGAGE_SCRIPT_SELECTOR) !== null;

    const matchConsent = (): void => {
        if (analyticsAllowed() === renderedWithAnalytics) {
            setReloadedFor(null);
            return;
        }
        if (reloadedFor() === location.href) {
            return;
        }
        setReloadedFor(location.href);
        location.reload();
    };

    matchConsent();
    document.addEventListener("cookieyes_consent_update", matchConsent);
}
